import { useSelector } from 'react-redux'
import { selectCurrentProject } from '@app/features/project/projectSlice'
import { BaseNumerableList, NavigationItem, RedirectableList } from './sideBarTypes'
import type { FastActivity } from '@typedefs/interfaces'
import { ProjectNavigation } from '@typedefs/interfaces'
import { getModuleNameFromBEId } from '@utils/moduleList'
import { useAppDispatch, useAppNavigate, useAppSelector, usePermissions } from '@app/hooks'
import { setModuleList } from '@app/features/builder/builderSlice'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { SideBarContext } from './provider'
import { useContextAwareLazyGetFastActivitiesByProject } from '@hooks/index'
import { encodeProjectId, encodeActivityId } from '@utils/projectIdEncoder'

const useGetMenuContent = () => {
	const { project } = useSelector(selectCurrentProject)
	const [activities, setActivities] = useState<FastActivity[]>([])
	const [hasInitiallyFetched, setHasInitiallyFetched] = useState<boolean>(false)
	const [currentProject, setCurrentProject] = useState(project?.id)
	const [
		getFastActivitiesByProject,
		{
			data: activitiesRes,
			isLoading,
			isFetching,
		}
	] = useContextAwareLazyGetFastActivitiesByProject();
	const { activity: currentActivity } = useAppSelector(selectCurrentProject)
	const { isPublic } = usePermissions();
	const { toggleSidebar, isSideBarOpen } = useContext(SideBarContext)
	const dispatch = useAppDispatch()
	const navigate = useAppNavigate();
	const encodedId = useMemo(() => project?.id ? encodeProjectId(project?.id) : undefined, [project?.id]);

	const enumerableProject = new BaseNumerableList(
		"project.project",
		[
			new NavigationItem(ProjectNavigation.Description, () => navigate({ path: `/project/${isPublic ? encodedId : project?.id}/description` })),
			new NavigationItem(ProjectNavigation.Activities, () => navigate({ path: `/project/${isPublic ? encodedId : project?.id}/activities` })),
			new NavigationItem(ProjectNavigation.Results, () => navigate({ path: `/project/${isPublic ? encodedId : project?.id}/results` }))
		]
	)


	const handleActivityClick = useCallback((activity: FastActivity, subModule?: string) => {
		//TODO: review new topbar logic and replicate the below comemented logic into the new logic or refactor

		// const feModuleList = getFEModuleListFromActivity(activity);
		// const subModulePath = subModule ? feModuleList.filter(el => el.name === subModule)[0]?.path : null;
		// dispatch(setActivity(activity));
		// dispatch(setModuleList(feModuleList));
		// const path = subModulePath ? `/builder/${activity.id}/${subModulePath}` : `/builder/${activity.id}/${feModuleList[0].path}`
		// navigate(feModuleList.length > 0 ? path : `/project/${activity.id}/complete`);

		const activityIdToUse = isPublic
			? (() => {
				try {
					return encodeActivityId(activity.id);
				} catch (error) {
					console.error('Failed to encode activity ID:', error);
					return activity.id.toString();
				}
			})()
			: activity.id;

		if (activity.id !== currentActivity?.id)
			dispatch(setModuleList([]))
		toggleSidebar()
		navigate({ path: `/builder/${activityIdToUse}` })
	}, [currentActivity?.id, dispatch, navigate, toggleSidebar, isPublic]);

	const [currentPage, setCurrentPage] = useState(1);

	const handleLoadMore = useCallback(() => {
		if (activitiesRes?.next && project?.id) {
			const nextPage = currentPage + 1;
			setCurrentPage(nextPage);
			getFastActivitiesByProject({
				projectId: Number(project?.id),
				page: nextPage,
				pageSize: 5,
			});
		}
	}, [activitiesRes?.next, getFastActivitiesByProject, project?.id, currentPage])

	useEffect(() => {
		if (currentProject !== project?.id) {
			setHasInitiallyFetched(false)
			setCurrentProject(project?.id)
			setCurrentPage(1)
		}

		if (isSideBarOpen && project?.id && !hasInitiallyFetched) {
			setActivities([])
			getFastActivitiesByProject({
				projectId: Number(project?.id),
				page: 1,
				pageSize: 5,
			})
			setHasInitiallyFetched(true)
		}
	}, [isSideBarOpen, project?.id, getFastActivitiesByProject, hasInitiallyFetched, currentProject])

	useEffect(() => {
		if (activitiesRes) {
			setActivities((prev) => [...prev, ...activitiesRes.results])
		}
	}, [activitiesRes])

	const enumerableActivities = useMemo(() => {
		return new BaseNumerableList(
			"activity.activities",
			activities.map(
				activity => new RedirectableList(
					activity.name,
					activity.module_types.map(
						module_type => new NavigationItem(getModuleNameFromBEId(module_type) ?? "", () => handleActivityClick(activity))
					),
					() => handleActivityClick(activity)
				)
			),
		)
	}, [activities, handleActivityClick])
	return {
		enumerableProject,
		enumerableActivities,
		isLoading: isLoading || isFetching,
		loadMore: handleLoadMore,
	}
}

export default useGetMenuContent